import { Modal, ModalBody, ModalFooter, Button, ModalHeader, Input, InputGroup, InputGroupText } from 'src/components/ui';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { UWEEntity } from '../../api/types';
import { useContext, useState } from 'react';
import { useTheme } from '@emotion/react';
import { useLingui } from '@lingui/react';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import { enUS, es } from 'date-fns/locale';
import UWEEntityApi from '../../api/UWEEntityApi';
import { AuthContext } from '../../context/AuthContext';
import Loader from '../../components/Loader';

import 'react-datepicker/dist/react-datepicker.css';
import { getDateFnsLocale, getDateFormat } from '../../components/customFields/DateWidget';
import { getTestId } from '../../hooks/useTestId';
import Icon from 'src/components/Icon';

const PARAMS = ['title', 'text', 'options', 'extraProps'] as const;

const OPTIONS = [
  { text: t`Cancel`, color: 'secondary', value: false },
  { text: t`Confirm Change`, color: 'primary', value: true },
];

interface ModalOption {
  text?: string;
  color?: string;
  onClick?: (args: { closeModal: (value?: boolean) => void }) => void;
  value?: boolean;
}

interface ExtraProps {
  reversible?: boolean;
}

interface UpdateThresholdValueModalProps {
  title?: string;
  text?: string;
  options?: ModalOption[];
  onConfirm: (value?: boolean) => void;
  extraProps?: ExtraProps;
  isOpen: boolean;
  getCurrentFormObject?: () => UWEEntity;
  testIdBase?: string;
}

type ChangeData = {
  value: number;
  date: Date | null;
};

// --- Locale helpers (mirrors DateWidget) ---

const DATE_FORMATS = {
  en: 'MM/dd/yyyy',
  es: 'dd/MM/yyyy',
} as const;

function UpdateThresholdValueModal({ onConfirm: onClose, isOpen, getCurrentFormObject, testIdBase = 'update-threshold-value-modal', ...props }: UpdateThresholdValueModalProps) {
  const auth = useContext(AuthContext);
  const theme = useTheme();
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const entity = getCurrentFormObject ? getCurrentFormObject() : null;

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const [changeData, setChangeData] = useState<ChangeData>({
    value: 0,
    date: null,
  });

  if (entity == null) {
    console.error('No entity found for UpdateThresholdValueModal');
    return null;
  }

  const title = entity.metadata.name;
  const dateFormat = getDateFormat(locale);
  const dateFnsLocale = getDateFnsLocale(locale);

  // Tomorrow as the minimum selectable date (mirrors the original min={dayjs().add(1, 'day')})
  const minDate = dayjs()
    .add(1, 'day')
    .startOf('day')
    .toDate();

  const handleButtonClick = async (actionConfirmed: boolean) => {
    if (actionConfirmed) {
      setLoading(true);
      setError(null);

      const data = {
        pendingChange: {
          effectiveDate: dayjs(changeData.date).format('YYYY-MM-DD'),
          thresholdValue: changeData.value / 100,
          lastUpdate: dayjs().format('YYYY-MM-DD'),
          lastUpdateUser: auth?.user?.user?.id,
          lastUpdateUserName: `${auth?.user?.user?.firstName} ${auth?.user?.user?.lastName}`,
        },
      };

      try {
        const wfAccessToken = await UWEEntityApi.createWfAccessTokenHash({
          workflowId: entity.workflowId,
          uweEntityId: entity.id,
          canView: true,
          canUpdate: true,
          canCreate: false,
        });
        await UWEEntityApi.updateUWEEntityData({ id: entity?.id, data, wfAccessToken });
      } catch (e) {
        // Save failed — keep the modal open and surface the error instead of proceeding
        // to close/submit as if the pending change had actually been persisted.
        setError(e as Error);
        setLoading(false);
        return;
      }

      setLoading(false);
    }

    onClose(actionConfirmed);
  };

  return (
    <Modal isOpen={isOpen} toggle={() => onClose()} className="modal-primary" size="md" backdrop style={{ position: 'relative' }}>
      {loading && (
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.1)',
            zIndex: 20,
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader spinner />
        </div>
      )}
      {title && <ModalHeader>{title}</ModalHeader>}
      <ModalBody className="text-center">
        {error && (
          <div className="alert alert-danger" role="alert">
            <Trans>Something went wrong while saving this change. Please try again.</Trans>
          </div>
        )}
        <div className="alert alert-danger pb-4" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', width: '50%', marginRight: 16 }}>
            <small style={{ textTransform: 'uppercase', color: theme.gray500, fontWeight: 500 }}>
              <Trans>Current Variation Limit</Trans>
            </small>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span className="text-danger text-bolder">{entity.metadata.thresholdValueText}</span>
              <small>
                <Trans>vs. previous period</Trans>
              </small>
            </div>
            <div className="mt-2">
              <small style={{ textTransform: 'uppercase', color: theme.gray500, fontWeight: 500 }}>
                <Trans>New Variation Limit</Trans>
              </small>
            </div>
            <div style={{ position: 'relative' }}>
              <div className="input-wrapper">
                <InputGroup className="mb-0">
                  <Input
                    className="ghost-input"
                    type="number"
                    min="0"
                    placeholder={t`Percent Limit`}
                    onChange={e =>
                      setChangeData({
                        date: changeData.date,
                        value: Number(e.target.value),
                      })
                    }
                    {...getTestId(testIdBase, '.', 'value-input')}
                  />
                  <InputGroupText className="input-group-append pr-2">
                    <Icon icon="Percent" style={{ color: theme.gray800 }} />
                  </InputGroupText>
                </InputGroup>
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              alignItems: 'flex-start',
              width: '50%',
              paddingLeft: 16,
            }}
          >
            <small style={{ textTransform: 'uppercase', color: theme.gray500, fontWeight: 500 }}>
              <Trans>Effective Since</Trans>
            </small>
            <strong>
              {dayjs(entity.data?.effectiveDate)
                .locale(i18n.locale)
                .format('ll')}
            </strong>
            <div className="mt-2">
              <small style={{ textTransform: 'uppercase', color: theme.gray500, fontWeight: 500 }}>
                <Trans>New Effective Date</Trans>
              </small>
            </div>
            <div className="w-full input-wrapper">
              <DatePicker
                locale={dateFnsLocale}
                dateFormat={dateFormat}
                selected={changeData.date}
                onChange={(date: Date | null) => setChangeData({ ...changeData, date })}
                minDate={minDate}
                placeholderText={t`Select a date`}
                className="form-control ghost-input w-full"
                wrapperClassName="block"
                showPopperArrow={false}
                showIcon
                icon={<Icon icon="Calendar" className="fa-regular" style={{ color: 'black', zIndex: 99 }} />}
                popperProps={{ strategy: 'fixed' }}
                popperPlacement="bottom-start"
                toggleCalendarOnIconClick
                showYearDropdown
                scrollableYearDropdown
              />
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button onClick={() => handleButtonClick(false)} color="secondary" {...getTestId(testIdBase, '.', 'cancel')}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={() => handleButtonClick(true)} color="primary" disabled={changeData.value <= 0 || changeData.date == null} {...getTestId(testIdBase, '.', 'confirm')}>
          <Trans>Confirm Change</Trans>
        </Button>
      </ModalFooter>
    </Modal>
  );
}

UpdateThresholdValueModal.PARAMS = PARAMS;
export default UpdateThresholdValueModal;
